import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { useList, errMsg, fmtRM, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner, EmptyState } from "@/components/common";
import { ChatWidget } from "@/components/ChatWidget";
import { toast } from "sonner";
import { Music4, Menu, X, CheckCircle2, Piano, CalendarHeart, Users2, ArrowRight, MapPin, Clock3, Phone, Sparkles, GraduationCap, ShieldCheck, BookOpen, Guitar, Mic, Music, Star, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  useEffect(() => {
    api.get("/public/site-settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);
  const s = siteSettings || {};
  const socials = [
    { url: s.facebook_url, label: "Facebook" },
    { url: s.instagram_url, label: "Instagram" },
    { url: s.tiktok_url, label: "TikTok" },
    { url: s.youtube_url, label: "YouTube" },
  ].filter((x) => x.url);
  const links = [
    { to: "/courses", label: "Music Courses" },
    { to: "/teachers", label: "Music Instructors" },
    { to: "/register", label: "Register Full Course" },
    { to: "/calendar", label: "Calendar" },
    { to: "/enquiry", label: "Contact" },
  ];
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[color:var(--v2-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between py-3.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-logo">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[color:var(--v2-primary)] to-[color:var(--v2-primary-dark)] flex items-center justify-center shadow-[0_4px_10px_rgba(91,42,134,0.25)]">
              <Music4 className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display font-bold leading-none text-[color:var(--v2-text)]">Cornerstone</p>
              <p className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--v2-primary)] font-semibold">Music School</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`public-nav-${l.to.slice(1)}`}
                className="px-3.5 py-2 text-sm font-medium text-[color:var(--v2-text)] hover:text-[color:var(--v2-primary)] rounded-md transition-colors">
                {l.label}
              </Link>
            ))}
            <Link to="/trial-booking"
                  className="ml-2 rounded-full border border-[color:var(--v2-primary)] text-[color:var(--v2-primary)] px-4 py-1.5 text-sm font-semibold hover:bg-[color:var(--v2-light-blue)] transition-colors"
                  data-testid="public-book-trial">
              Book a Free Trial
            </Link>
            <Link to="/register"
                  className="rounded-full bg-[color:var(--v2-primary)] hover:bg-[color:var(--v2-primary-dark)] text-white px-5 py-1.5 text-sm font-semibold shadow-[0_2px_8px_rgba(91,42,134,0.25)] hover:-translate-y-0.5 transition-all"
                  data-testid="public-enrol-now">
              Enrol Now
            </Link>
          </nav>
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)} data-testid="public-mobile-menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
        {open && (
          <div className="md:hidden border-t bg-white px-4 py-3 space-y-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="block px-3 py-2 text-sm font-medium rounded-md hover:bg-[color:var(--v2-light-blue)]">{l.label}</Link>
            ))}
            <div className="pt-2 flex gap-2">
              <Link to="/trial-booking" onClick={() => setOpen(false)}
                    className="flex-1 text-center rounded-full border border-[color:var(--v2-primary)] text-[color:var(--v2-primary)] py-2 text-sm font-semibold">
                Book a Free Trial
              </Link>
              <Link to="/register" onClick={() => setOpen(false)}
                    className="flex-1 text-center rounded-full bg-[color:var(--v2-primary)] text-white py-2 text-sm font-semibold">
                Enrol Now
              </Link>
            </div>
          </div>
        )}
      </header>
      <main>{children}</main>
      <ChatWidget />
      <footer className="bg-[color:var(--v2-soft-grey)] border-t border-[color:var(--v2-border)] mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid sm:grid-cols-2 md:grid-cols-5 gap-8 text-sm">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[color:var(--v2-primary)] to-[color:var(--v2-primary-dark)] flex items-center justify-center">
                <Music4 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-display font-bold leading-none text-[color:var(--v2-text)]">Cornerstone</p>
                <p className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--v2-primary)] font-semibold">Music School</p>
              </div>
            </Link>
            <p className="text-[color:var(--v2-text-muted)] leading-relaxed max-w-sm">
              Yamaha-authorised music academy in Negeri Sembilan. Piano, guitar, violin, drums and vocal, taught with heart to students of every age.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[color:var(--v2-text)] mb-3">Courses</p>
            <ul className="space-y-2 text-[color:var(--v2-text-muted)]">
              <li><Link to="/courses" className="hover:text-[color:var(--v2-primary)]">Piano</Link></li>
              <li><Link to="/courses" className="hover:text-[color:var(--v2-primary)]">Guitar</Link></li>
              <li><Link to="/courses" className="hover:text-[color:var(--v2-primary)]">Drums</Link></li>
              <li><Link to="/courses" className="hover:text-[color:var(--v2-primary)]">Violin</Link></li>
              <li><Link to="/courses" className="hover:text-[color:var(--v2-primary)]">Vocal</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[color:var(--v2-text)] mb-3">About</p>
            <ul className="space-y-2 text-[color:var(--v2-text-muted)]">
              <li><Link to="/teachers" className="hover:text-[color:var(--v2-primary)]">Music Instructors</Link></li>
              <li><Link to="/calendar" className="hover:text-[color:var(--v2-primary)]">Calendar</Link></li>
              <li><Link to="/enquiry" className="hover:text-[color:var(--v2-primary)]">Contact Us</Link></li>
              <li><Link to="/login" className="hover:text-[color:var(--v2-primary)]">Parent Portal</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-[color:var(--v2-text)] mb-3">Contact</p>
            <ul className="space-y-2 text-[color:var(--v2-text-muted)]">
              <li>{s.address_line1 || "Premiera 3A, Bandar Enstek"}</li>
              <li>{s.address_line2 || "71760 Negeri Sembilan"}</li>
              <li>{s.email || "hello@cornerstonemusic.com.my"}</li>
              <li>{s.phone || "012-345 6789"}</li>
            </ul>
            {socials.length > 0 && (
              <div className="flex gap-2 mt-3" data-testid="footer-socials">
                {socials.map((so) => (
                  <a key={so.label} href={so.url} target="_blank" rel="noopener noreferrer"
                     className="h-8 px-3 rounded-full bg-[color:var(--v2-primary)] text-white text-xs font-semibold flex items-center hover:bg-[color:var(--v2-primary-dark)]">
                    {so.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-[color:var(--v2-border)] py-4 text-center text-xs text-[color:var(--v2-text-muted)]">
          © {new Date().getFullYear()} Cornerstone Music School · Yamaha-Authorised Music School
        </div>
      </footer>
    </div>
  );
}

export function LandingPage() {
  const [siteSettings, setSiteSettings] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    api.get("/public/site-settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);
  const s = siteSettings || {};
  const heroImages = (s.hero_images && s.hero_images.length > 0)
    ? s.hero_images
    : [{ url: "https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?auto=format&fit=crop&w=1200&q=70",
         alt: "Mother teaching daughter piano at Cornerstone Music" }];
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, [heroImages.length]);
  const faqs = (s.faqs && s.faqs.length > 0) ? s.faqs : [
    { q: "What ages do you teach?", a: "We teach students from age 3 to adults of any age." },
    { q: "Do you offer trial lessons?", a: "Yes! Book a free trial lesson online any time." },
    { q: "Are the teachers certified?", a: "All teachers are Yamaha-authorised and hold music qualifications." },
  ];
  const { data: courses } = useList("/public/courses");
  // Featured courses: use admin-picked tiles when configured, otherwise fall
  // back to the hardcoded showcase below.
  const featuredIds = s.featured_course_ids || [];
  const featuredCourses = featuredIds.length > 0 && Array.isArray(courses)
    ? featuredIds.map((id) => courses.find((c) => c.id === id)).filter(Boolean)
    : [];
  const iconForCourse = (c) => {
    const n = (c.name || "").toLowerCase() + " " + (c.category || "").toLowerCase();
    if (/piano|electone|keyboard/.test(n)) return Piano;
    if (/guitar/.test(n)) return Guitar;
    if (/vocal|sing|voice/.test(n)) return Mic;
    return Music;
  };
  return (
    <PublicLayout>
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-8 lg:pt-14 pb-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <p className="text-[11px] font-bold tracking-[0.28em] text-[color:var(--v2-primary)] uppercase mb-4" data-testid="hero-tagline">
              Learn. Grow. Express.
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[color:var(--v2-text)] leading-[1.05] tracking-tight" data-testid="hero-headline">
              Music education
              <br /><span className="text-[color:var(--v2-primary)]">for every stage of life.</span>
            </h1>
            <p className="mt-5 text-base text-[color:var(--v2-text-muted)] leading-relaxed max-w-lg">
              Inspiring lessons, personalised guidance and a nurturing community — helping students discover confidence, creativity and a lifelong love of music.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/trial-booking"
                    className="inline-flex items-center gap-2 rounded-full bg-[color:var(--v2-primary)] hover:bg-[color:var(--v2-primary-dark)] text-white px-6 py-3 text-sm font-semibold shadow-[0_4px_12px_rgba(91,42,134,0.25)] hover:-translate-y-0.5 transition-all"
                    data-testid="hero-book-trial">
                Book a Free Trial Lesson <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/courses"
                    className="inline-flex items-center rounded-full border border-[color:var(--v2-border)] text-[color:var(--v2-text)] px-6 py-3 text-sm font-semibold hover:bg-[color:var(--v2-light-blue)] transition-colors"
                    data-testid="hero-explore">
                Explore Courses
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex -space-x-2">
                {[0,1,2,3].map(i => (
                  <div key={i} className="h-8 w-8 rounded-full bg-[color:var(--v2-light-blue)] border-2 border-white flex items-center justify-center">
                    <span className="text-[10px] font-semibold text-[color:var(--v2-primary)]">{["MW","JT","SL","AK"][i]}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[0,1,2,3,4].map(i => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
                  <span className="ml-1 text-xs font-bold text-[color:var(--v2-text)]">4.9/5</span>
                </div>
                <p className="text-[11px] text-[color:var(--v2-text-muted)]">Trusted by hundreds of families in Malaysia</p>
              </div>
            </div>
          </div>
          <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_40px_rgba(20,33,61,0.12)] aspect-[4/3] lg:aspect-[5/4]">
            {heroImages.map((im, i) => (
              <img key={im.url}
                   src={im.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${im.url}` : im.url}
                   alt={im.alt || "Cornerstone Music"}
                   className={"absolute inset-0 h-full w-full object-cover transition-opacity duration-700 " + (i === heroIdx ? "opacity-100" : "opacity-0")}
                   data-testid={i === heroIdx ? "hero-image" : `hero-image-${i}`} />
            ))}
            {heroImages.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {heroImages.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)}
                          className={"h-1.5 rounded-full transition-all " + (i === heroIdx ? "w-6 bg-white" : "w-1.5 bg-white/60 hover:bg-white/80")}
                          data-testid={`hero-dot-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── FEATURE STRIP ─────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <div className="rounded-2xl border border-[color:var(--v2-border)] bg-white grid grid-cols-2 md:grid-cols-4 gap-2 p-5 shadow-[0_1px_2px_rgba(20,33,61,0.04)]">
          {[
            { icon: Users2, title: "Qualified Instructors", body: "Caring, experienced teachers who bring out the best in every student." },
            { icon: CalendarHeart, title: "Flexible & Convenient", body: "Lessons that fit your schedule with make-up options and easy rescheduling." },
            { icon: BookOpen, title: "Structured Curriculum", body: "Proven methods and progress pathways for every level and age." },
            { icon: ShieldCheck, title: "Yamaha Authorised", body: "Official Yamaha music school using world-class methods and standards." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 p-3" data-testid={`feature-${i}`}>
              <div className="h-11 w-11 shrink-0 rounded-full bg-[color:var(--v2-light-blue)] flex items-center justify-center">
                <f.icon className="h-5 w-5 text-[color:var(--v2-primary)]" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-[color:var(--v2-text)]">{f.title}</p>
                <p className="text-[12px] text-[color:var(--v2-text-muted)] leading-snug mt-0.5">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── COURSES ───────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <div className="text-center mb-8">
          <p className="text-[11px] font-bold tracking-[0.28em] text-[color:var(--v2-primary)] uppercase mb-2">Our Courses</p>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[color:var(--v2-text)]">Find the right course for you</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {(featuredCourses.length > 0
            ? featuredCourses.map((c) => ({
                icon: iconForCourse(c),
                name: c.name,
                img: c.image_url ? mediaUrl(c.image_url) : null,
                tag: c.category || "Course",
                to: c.is_master ? `/register?master=${encodeURIComponent(c.master_course_name || c.name)}`
                                : `/register?courseId=${c.id}`,
                testId: `course-card-${c.id}`,
              }))
            : [
                { icon: Piano, name: "Piano Course", img: "https://images.unsplash.com/photo-1552422535-c45813c61732?auto=format&fit=crop&w=800&q=75", tag: "Build a strong foundation and play with confidence.", to: "/courses", testId: "course-card-piano" },
                { icon: Music, name: "Electone Course", img: "https://images.unsplash.com/photo-1552422535-c45813c61732?auto=format&fit=crop&w=800&q=75&sat=-30&hue=270", tag: "Yamaha's signature keyboard for creative expression.", to: "/courses", testId: "course-card-electone" },
                { icon: Guitar, name: "Classical Guitar", img: "https://images.unsplash.com/photo-1510915361894-db8b60106cb1?auto=format&fit=crop&w=800&q=75", tag: "Timeless technique with nylon-string classical guitar.", to: "/courses", testId: "course-card-classical-guitar" },
                { icon: Guitar, name: "Acoustic Guitar", img: "https://customer-assets-jai6qajn.emergentagent.net/job_musicschool-pro/artifacts/przuetb0_0ca36750-bf40-4ea1-917b-dadd2c8ab0d7.png", tag: "Strum chords, sing along and play your favourite songs.", to: "/courses", testId: "course-card-acoustic-guitar" },
                { icon: Guitar, name: "Electric Guitar", img: "https://images.unsplash.com/photo-1550985616-10810253b84d?auto=format&fit=crop&w=800&q=75", tag: "Modern riffs, effects and stage-ready performance skills.", to: "/courses", testId: "course-card-electric-guitar" },
                { icon: Music, name: "Drum Course", img: "https://images.unsplash.com/photo-1543443258-92b04ad5ec6b?auto=format&fit=crop&w=800&q=75", tag: "Develop rhythm, timing and coordination.", to: "/courses", testId: "course-card-drum" },
                { icon: Music, name: "Flute Course", img: "https://customer-assets-jai6qajn.emergentagent.net/job_musicschool-pro/artifacts/trvds42g_Flute.png", tag: "Elegant, expressive and versatile — solo or ensemble.", to: "/courses", testId: "course-card-flute" },
                { icon: Music, name: "Violin Course", img: "https://images.unsplash.com/photo-1465847899084-d164df4dedc6?auto=format&fit=crop&w=800&q=75", tag: "Cultivate technique, musicianship and expression.", to: "/courses", testId: "course-card-violin" },
              ]
          ).map((c) => (
            <Link key={c.testId || c.name} to={c.to || "/courses"}
                  className="group rounded-2xl border border-[color:var(--v2-border)] bg-white overflow-hidden hover:-translate-y-1 hover:shadow-[0_10px_24px_rgba(20,33,61,0.10)] transition-all"
                  data-testid={c.testId || `course-card-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                {c.img ? (
                  <img src={c.img} alt={c.name} loading="lazy"
                       className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[color:var(--v2-light-blue)] to-white flex items-center justify-center">
                    <c.icon className="h-10 w-10 text-[color:var(--v2-primary)]/40" />
                  </div>
                )}
                <div className="absolute top-3 left-3 h-9 w-9 rounded-full bg-[color:var(--v2-primary)] flex items-center justify-center shadow-lg">
                  <c.icon className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="p-4">
                <p className="font-semibold text-[color:var(--v2-text)]">{c.name}</p>
                <p className="text-[12px] text-[color:var(--v2-text-muted)] mt-0.5 leading-snug">{c.tag}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-6">
          <Link to="/courses" className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--v2-primary)] hover:underline">
            View all courses <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ─── YAMAHA ADVANTAGE ──────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="rounded-2xl bg-[color:var(--v2-light-blue)] border border-[color:var(--v2-accent-blue)]/40 p-6 lg:p-8 grid lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="rounded-md bg-[color:var(--v2-primary)] text-white px-2 py-0.5 text-[10px] font-bold tracking-widest">YAMAHA</span>
              <span className="text-[10px] font-bold tracking-[0.25em] text-[color:var(--v2-primary)] uppercase">Advantage</span>
            </div>
            <h3 className="font-display text-xl lg:text-2xl font-semibold text-[color:var(--v2-text)]">The Yamaha Music Education Advantage</h3>
            <p className="text-sm text-[color:var(--v2-text-muted)] mt-2">
              Our Yamaha-authorised curriculum develops musicality, creativity and character. Students enjoy a structured journey with internationally recognised methods trusted by millions around the world.
            </p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: BookOpen, title: "Musical Foundation", body: "Strong skills for lifelong learning" },
              { icon: Sparkles, title: "Creative Expression", body: "Encouraging imagination and self-expression" },
              { icon: ShieldCheck, title: "Confidence & Discipline", body: "Building focus, perseverance and poise" },
              { icon: GraduationCap, title: "Global Recognition", body: "Internationally respected standards and exams" },
            ].map((f, i) => (
              <div key={i} className="bg-white/70 rounded-xl p-3">
                <f.icon className="h-5 w-5 text-[color:var(--v2-primary)] mb-1.5" strokeWidth={1.75} />
                <p className="font-semibold text-[13px] text-[color:var(--v2-text)]">{f.title}</p>
                <p className="text-[11px] text-[color:var(--v2-text-muted)] mt-0.5 leading-snug">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── VISIT / HOURS / FAQ ───────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-[color:var(--v2-border)] bg-white p-5">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--v2-primary)]">
            <MapPin className="h-4 w-4" />
            <p className="font-semibold text-sm">Visit Us</p>
          </div>
          <p className="font-semibold text-[color:var(--v2-text)]">{s.business_name || "Cornerstone Music School"}</p>
          <p className="text-sm text-[color:var(--v2-text-muted)] mt-1 leading-relaxed">
            {s.address_line1 || "Premiera 3A, Bandar Enstek"}<br />{s.address_line2 || "71760 Negeri Sembilan"}
          </p>
          {s.map_embed_url && s.map_embed_url.startsWith("https://www.google.com/maps/") && (
            <div className="mt-3 rounded-lg overflow-hidden border border-[color:var(--v2-border)] aspect-[16/10] bg-slate-50">
              <iframe title={`${s.business_name || "Cornerstone"} location map`}
                      src={s.map_embed_url}
                      loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                      className="h-full w-full border-0"
                      data-testid="visit-us-map" />
            </div>
          )}
          <a href={s.map_embed_url && s.map_embed_url.startsWith("https://www.google.com/maps/")
                   ? s.map_embed_url.replace(/\/embed\?/, "/place/?").split("&")[0]
                   : "https://maps.google.com"}
             target="_blank" rel="noopener noreferrer"
             className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--v2-primary)] hover:underline">
            Get Directions <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="rounded-2xl border border-[color:var(--v2-border)] bg-white p-5">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--v2-primary)]">
            <Clock3 className="h-4 w-4" />
            <p className="font-semibold text-sm">Opening Hours</p>
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between"><span className="text-[color:var(--v2-text-muted)]">Mon – Fri</span><span className="font-semibold text-[color:var(--v2-text)]">{s.hours_mon_fri || "1:00pm – 9:00pm"}</span></li>
            <li className="flex justify-between"><span className="text-[color:var(--v2-text-muted)]">Sat</span><span className="font-semibold text-[color:var(--v2-text)]">{s.hours_sat || "9:30am – 6:00pm"}</span></li>
            <li className="flex justify-between"><span className="text-[color:var(--v2-text-muted)]">Sun</span><span className="font-semibold text-[color:var(--v2-text)]">{s.hours_sun || "9:30am – 1:00pm"}</span></li>
            <li className="flex justify-between"><span className="text-[color:var(--v2-text-muted)]">Public Holiday</span><span className="font-semibold text-rose-600">{s.hours_ph || "Closed"}</span></li>
          </ul>
          <p className="text-[11px] text-[color:var(--v2-text-muted)] mt-3">* Hours may vary. Please contact us to confirm.</p>
        </div>
        <div className="rounded-2xl border border-[color:var(--v2-border)] bg-white p-5">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--v2-primary)]">
            <HelpCircle className="h-4 w-4" />
            <p className="font-semibold text-sm">Frequently Asked Questions</p>
          </div>
          <Accordion type="single" collapsible>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[color:var(--v2-border)]">
                <AccordionTrigger className="text-left text-sm font-medium py-2.5 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[color:var(--v2-text-muted)]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/enquiry" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--v2-primary)] hover:underline">
            View all FAQs <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* ─── CTA BAR ───────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-14">
        <div className="rounded-2xl px-6 py-6 lg:px-10 lg:py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 text-white relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, #5B2A86 0%, #3E1C5E 100%)" }}>
          <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <CalendarHeart className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg sm:text-xl font-semibold">Ready to begin your musical journey?</h3>
            <p className="text-sm text-white/80 mt-1">Book a free trial lesson today and experience the Cornerstone difference.</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Link to="/trial-booking"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[color:var(--v2-text)] px-5 py-2.5 text-sm font-semibold"
                  data-testid="cta-book-trial">
              Book a Free Trial Lesson <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/register"
                  className="inline-flex items-center rounded-full border border-white/40 text-white px-5 py-2.5 text-sm font-semibold hover:bg-white/10"
                  data-testid="cta-enrol">
              Enrol Now
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export function PublicCourses() {
  const { data: courses, loading } = useList("/public/courses");
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Our Music Courses</h1>
        {loading ? <Spinner full /> : !courses || courses.length === 0 ? <EmptyState text="Music Courses coming soon." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="public-courses-grid">
            {courses.map((c) => (
              <div key={c.id} className="rounded-lg border bg-card flex flex-col lift overflow-hidden" data-testid={`public-course-${c.id}`}>
                {c.image_url
                  ? <img src={mediaUrl(c.image_url)} alt={c.image_alt_text || c.name} loading="lazy" className="w-full h-44 object-cover" />
                  : <div className="w-full h-44 bg-gradient-to-br from-secondary/15 to-secondary/5 flex items-center justify-center"><Music4 className="h-10 w-10 text-secondary/40" /></div>}
                <div className="p-8 flex flex-col flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">{c.category}</p>
                  <h3 className="text-2xl font-semibold mb-2">{c.name}</h3>
                  <p className="text-sm text-muted-foreground flex-1">{c.description}</p>
                  {(c.age_group || c.lesson_duration) && (
                    <p className="text-xs text-muted-foreground mt-3">
                      {c.age_group && <>Age {c.age_group}</>}{c.age_group && c.lesson_duration && " · "}{c.lesson_duration}
                    </p>
                  )}
                  <div className="mt-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-muted-foreground">From</p>
                      <p className="text-xl font-bold font-display">{c.fee_from ? `${fmtRM(c.fee_from)}/mo` : "Enquire"}</p>
                    </div>
                    <Button asChild size="sm" className="w-full mt-3 bg-secondary hover:bg-[#D96C52]" data-testid={`register-course-${c.id}`}>
                      <Link to={c.is_master ? `/register?master=${encodeURIComponent(c.name)}` : `/register?courseId=${c.id}`}>
                        Register Now <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

export function PublicTeachers() {
  const { data: teachers, loading } = useList("/public/teachers");
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Meet our Music Instructors</h1>
        <p className="text-muted-foreground mb-10 max-w-xl">Every Music Instructor is certified, background-checked and loves working with young musicians.</p>
        {loading ? <Spinner full /> : !teachers || teachers.length === 0 ? <EmptyState text="Music Instructor profiles coming soon." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-in" data-testid="public-teachers-grid">
            {teachers.map((t) => (
              <div key={t.id} className="rounded-lg border bg-card p-8 lift flex flex-col" data-testid={`public-teacher-${t.id}`}>
                <div className="flex items-center gap-4 mb-4">
                  {t.profile_photo_url ? (
                    <img src={mediaUrl(t.profile_photo_url)} alt={t.full_name} className="h-14 w-14 rounded-full object-cover border" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-accent flex items-center justify-center font-display font-bold text-primary">
                      {t.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold">{t.full_name}</h3>
                    {t.years_experience != null && (
                      <p className="text-xs font-bold uppercase tracking-wider text-secondary">{t.years_experience}+ years experience</p>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{t.short_bio || t.bio}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {t.slug && (
                    <Button asChild size="sm" variant="outline" data-testid={`view-teacher-${t.id}`}>
                      <Link to={`/teachers/${t.slug}`}>View Music Instructor Profile <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                    </Button>
                  )}
                  <Button asChild size="sm" className="bg-secondary hover:bg-[#D96C52]" data-testid={`register-teacher-${t.id}`}>
                    <Link to={`/register?teacherId=${t.id}`}>Register with this Music Instructor</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}

function EnquiryForm({ type }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data: courses } = useList("/public/courses");
  const [form, setForm] = useState(() => ({
    course_interest: state?.course_interest || "",
    preferred_teacher: state?.preferred_teacher || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.phone) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      await api.post("/public/enquiries", { ...form, enquiry_type: type });
      navigate("/confirmation", { state: { type } });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid={`${type}-form`}>
      {form.preferred_teacher && (
        <p className="text-sm rounded-md bg-secondary/10 text-secondary font-medium px-3 py-2" data-testid="preferred-teacher-note">
          Requested teacher: {form.preferred_teacher}
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parent name *</Label>
          <Input data-testid="enquiry-parent-name" value={form.parent_name || ""} onChange={(e) => set("parent_name", e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone *</Label>
          <Input data-testid="enquiry-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
          <Input data-testid="enquiry-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Child's name & age</Label>
          <Input data-testid="enquiry-student-name" value={form.student_name || ""} onChange={(e) => set("student_name", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Course interest</Label>
          <select data-testid="enquiry-course" className="w-full border rounded-md px-3 py-2 text-sm bg-card h-9"
            value={form.course_interest || ""} onChange={(e) => set("course_interest", e.target.value)}>
            <option value="">Select...</option>
            {(courses || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred day</Label>
          <select data-testid="enquiry-preferred-day" className="w-full border rounded-md px-3 py-2 text-sm bg-card h-9"
            value={form.preferred_day || ""} onChange={(e) => set("preferred_day", e.target.value)}>
            <option value="">Any day</option>
            {["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred time</Label>
          <Input data-testid="enquiry-preferred-time" placeholder="e.g. Saturday morning" value={form.preferred_time || ""} onChange={(e) => set("preferred_time", e.target.value)} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message</Label>
        <Textarea data-testid="enquiry-message" rows={3} value={form.message || ""} onChange={(e) => set("message", e.target.value)} />
      </div>
      <Button type="submit" disabled={busy} size="lg" data-testid="enquiry-submit-button">
        {busy ? "Sending..." : type === "trial" ? "Book trial class" : "Send enquiry"}
      </Button>
    </form>
  );
}

export function EnquiryPage() {
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Get in touch</h1>
        <p className="text-muted-foreground mb-10 max-w-xl">
          Questions about courses, fees or schedules? Drop us a message.
        </p>
        <EnquiryForm type="enquiry" />
      </div>
    </PublicLayout>
  );
}

export function ConfirmationPage() {
  const { state } = useLocation();
  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="confirmation-page">
        <CheckCircle2 className="h-14 w-14 text-[#2D6A4F] mx-auto mb-6" strokeWidth={1.5} />
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">
          {state?.type === "trial" ? "Trial class request received!" : "Thank you for your enquiry!"}
        </h1>
        <p className="text-muted-foreground mb-8">
          Our team will contact you within one working day to confirm the details.
        </p>
        <Button asChild data-testid="confirmation-home-button"><Link to="/">Back to home</Link></Button>
      </div>
    </PublicLayout>
  );
}
